import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import zh from './locales/zh.json';
import en from './locales/en.json';
import ja from './locales/ja.json';
import es from './locales/es.json';
import ko from './locales/ko.json';
import fr from './locales/fr.json';
import ar from './locales/ar.json';
import zhTW from './locales/zh-TW.json';
import de from './locales/de.json';
import pt from './locales/pt.json';

const resources = {
  zh: { translation: zh },
  en: { translation: en },
  ja: { translation: ja },
  es: { translation: es },
  ko: { translation: ko },
  fr: { translation: fr },
  ar: { translation: ar },
  'zh-TW': { translation: zhTW },
  de: { translation: de },
  pt: { translation: pt },
};

export const supportedLanguages = ['en', 'zh', 'zh-TW', 'ja', 'es', 'ko', 'fr', 'ar', 'de', 'pt'];
const supportedLanguageMap = new Map(
  supportedLanguages.map((language) => [language.toLowerCase(), language])
);

export const LANGUAGE_FAMILY_MAP = {
  zh: 'zh',
  'zh-cn': 'zh',
  'zh-sg': 'zh',
  'zh-hans': 'zh',
  'zh-hans-cn': 'zh',
  'zh-hans-sg': 'zh',
  'zh-tw': 'zh-TW',
  'zh-hk': 'zh-TW',
  'zh-mo': 'zh-TW',
  'zh-hant': 'zh-TW',
  'zh-hant-tw': 'zh-TW',
  'zh-hant-hk': 'zh-TW',
  'zh-hant-mo': 'zh-TW',
  en: 'en',
  'en-us': 'en',
  'en-gb': 'en',
  'en-au': 'en',
  'en-ca': 'en',
  'en-nz': 'en',
  'en-in': 'en',
  ja: 'ja',
  'ja-jp': 'ja',
  es: 'es',
  'es-es': 'es',
  'es-mx': 'es',
  'es-ar': 'es',
  'es-cl': 'es',
  'es-co': 'es',
  'es-pe': 'es',
  ar: 'ar',
  'ar-sa': 'ar',
  'ar-ae': 'ar',
  'ar-eg': 'ar',
  'ar-qa': 'ar',
  'ar-kw': 'ar',
  de: 'de',
  'de-de': 'de',
  'de-at': 'de',
  'de-ch': 'de',
  fr: 'fr',
  'fr-fr': 'fr',
  'fr-ca': 'fr',
  'fr-be': 'fr',
  'fr-ch': 'fr',
  ko: 'ko',
  'ko-kr': 'ko',
  pt: 'pt',
  'pt-pt': 'pt',
  'pt-br': 'pt',
};

export const PRIMARY_LANGUAGE_MAP = {
  zh: 'zh',
  en: 'en',
  ja: 'ja',
  es: 'es',
  ar: 'ar',
  de: 'de',
  fr: 'fr',
  ko: 'ko',
  pt: 'pt',
};

const matchSupportedLanguage = (value) => {
  const input = String(value || '').trim();
  if (!input) {
    return null;
  }

  const normalized = input.toLowerCase().replace(/_/g, '-');
  const mappedLanguage = LANGUAGE_FAMILY_MAP[normalized];
  if (mappedLanguage) {
    return mappedLanguage;
  }

  const exactMatch = supportedLanguageMap.get(normalized);
  if (exactMatch) {
    return exactMatch;
  }

  const parts = normalized.split('-').filter(Boolean);
  const primary = parts[0];
  if (!primary) {
    return null;
  }

  if (primary === 'zh') {
    if (parts.includes('hant')) {
      return 'zh-TW';
    }

    if (parts.includes('hans')) {
      return 'zh';
    }

    const region = parts.find((part) => part.length === 2 || part.length === 3);
    if (region === 'tw' || region === 'hk' || region === 'mo') {
      return 'zh-TW';
    }

    if (region === 'cn' || region === 'sg') {
      return 'zh';
    }

    return 'zh';
  }

  return PRIMARY_LANGUAGE_MAP[primary] || supportedLanguageMap.get(primary) || null;
};

export const resolveSupportedLanguage = (value) => {
  return matchSupportedLanguage(value) || 'en';
};

export const resolvePreferredLanguage = (values) => {
  for (const value of values || []) {
    const resolved = matchSupportedLanguage(value);
    if (resolved) {
      return resolved;
    }
  }

  return 'en';
};

const detectInitialLanguage = () => {
  if (typeof window === 'undefined') {
    return 'en';
  }

  try {
    const stored = window.localStorage.getItem('i18nextLng');
    if (stored) {
      return resolveSupportedLanguage(stored);
    }

    const legacyStored = window.localStorage.getItem('language');
    if (legacyStored) {
      return resolveSupportedLanguage(legacyStored);
    }
  } catch (error) {
    // Ignore storage access failures.
  }

  const queryLanguage = new URLSearchParams(window.location.search).get('lng');
  if (queryLanguage) {
    return resolveSupportedLanguage(queryLanguage);
  }

  const navigatorLanguages = Array.isArray(window.navigator?.languages) && window.navigator.languages.length > 0
    ? window.navigator.languages
    : [window.navigator?.language];

  return resolvePreferredLanguage(navigatorLanguages);
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    lng: detectInitialLanguage(),
    fallbackLng: 'en',
    supportedLngs: supportedLanguages,
    nonExplicitSupportedLngs: true,
    initImmediate: false,
    detection: {
      order: ['localStorage', 'querystring', 'navigator'],
      lookupQuerystring: 'lng',
      lookupLocalStorage: 'i18nextLng',
      caches: ['localStorage'],
    },
    react: { useSuspense: false },
    interpolation: { escapeValue: false },
    returnNull: false,
  });

export default i18n;
